import { ReactNode, useEffect, useState } from 'react'
import DeleteIcon from '@mui/icons-material/Delete'
import JsonCodeEditor from '../JsonCodeEditor'
import { ApiFailureRuleMatch } from '../../types/api'
import { Autocomplete, Box, Button, Checkbox, FormControl, FormControlLabel, Grid, IconButton, InputLabel, List, ListItem, ListItemText, MenuItem, Select, TextField } from '@mui/material'

type MatchType = 'empty_content' | 'field_value' | 'status_code'

function Highlight({ children }: { children: ReactNode }) {
  return <Box component="span" sx={{ fontWeight: 600 }}>{children}</Box>
}

export function describeMatch(match: ApiFailureRuleMatch): ReactNode {
  const requerySuffix = match.requery_eligible ? <> — <Highlight>permite reconsulta</Highlight></> : null

  if (match.empty_content) {
    return <>Quando o retorno vier vazio{requerySuffix}</>
  }
  if (match.content_path && match.value_in) {
    return (
      <>
        Quando <Highlight>"{match.content_path.join('.')}"</Highlight> for um dos valores: <Highlight>{match.value_in.join(', ')}</Highlight>{requerySuffix}
      </>
    )
  }
  if (match.status_code_in) {
    return (
      <>
        Quando o status for um dos códigos: <Highlight>{match.status_code_in.join(', ')}</Highlight>{requerySuffix}
      </>
    )
  }
  return 'Condição em formato avançado (não editável aqui)'
}

type FailureConditionsBuilderProps = {
  rules: ApiFailureRuleMatch[]
  onChange: (rules: ApiFailureRuleMatch[]) => void
}

export default function FailureConditionsBuilder({ rules, onChange }: FailureConditionsBuilderProps) {
  const [matchType, setMatchType] = useState<MatchType>('empty_content')
  const [contentPath, setContentPath] = useState('')
  const [valueIn, setValueIn] = useState<string[]>([])
  const [statusCodes, setStatusCodes] = useState<string[]>([])
  const [requeryEligible, setRequeryEligible] = useState(false)
  const [rulesText, setRulesText] = useState(() => JSON.stringify(rules, null, 2))
  const [jsonError, setJsonError] = useState<string | null>(null)

  useEffect(() => {
    setRulesText(JSON.stringify(rules, null, 2))
    setJsonError(null)
  }, [rules])

  function handleRulesTextChange(value: string) {
    setRulesText(value)

    if (value.trim().length === 0) {
      setJsonError('O campo está vazio. Deixe pelo menos "[]" (lista vazia) ou monte as condições pelos campos ao lado.')
      return
    }

    try {
      const parsed = JSON.parse(value)
      if (!Array.isArray(parsed)) {
        setJsonError('O JSON precisa ser uma lista de condições.')
        return
      }
      setJsonError(null)
      onChange(parsed)
    } catch (e: any) {
      setJsonError('JSON inválido — confira se as chaves { }, colchetes [ ] e vírgulas estão todos certinhos.')
    }
  }

  const hasEmptyContentRule = rules.some((rule) => rule.empty_content)

  useEffect(() => {
    if (matchType === 'empty_content' && hasEmptyContentRule) {
      setMatchType('field_value')
    }
  }, [matchType, hasEmptyContentRule])

  function resetForm() {
    setMatchType(hasEmptyContentRule ? 'field_value' : 'empty_content')
    setContentPath('')
    setValueIn([])
    setStatusCodes([])
    setRequeryEligible(false)
  }

  function removeRule(index: number) {
    onChange(rules.filter((_, i) => i !== index))
  }

  function buildMatch(): ApiFailureRuleMatch | undefined {
    let match: ApiFailureRuleMatch | undefined

    if (matchType === 'empty_content') {
      match = { empty_content: true }
    } else if (matchType === 'field_value') {
      if (contentPath.trim() && valueIn.length > 0) {
        match = {
          content_path: contentPath.split('.').map((part) => part.trim()).filter(Boolean),
          value_in: valueIn,
        }
      }
    } else if (matchType === 'status_code') {
      const codes = statusCodes.map((code) => Number(code)).filter((code) => !Number.isNaN(code))
      if (codes.length > 0) {
        match = { status_code_in: codes }
      }
    }

    if (match && requeryEligible) {
      match.requery_eligible = true
    }

    return match
  }

  function addRule() {
    const match = buildMatch()
    if (!match) return

    onChange([...rules, match])
    resetForm()
  }

  const addDisabled =
    (matchType === 'field_value' && (!contentPath.trim() || valueIn.length === 0)) ||
    (matchType === 'status_code' && statusCodes.length === 0)

  return (
    <Box>
      {rules.length > 0 && (
        <List dense disablePadding sx={{ marginBottom: 2 }}>
          {rules.map((rule, index) => (
            <ListItem
              key={index}
              disablePadding
              sx={{ paddingY: 0.5 }}
              secondaryAction={
                <IconButton edge="end" aria-label="remover condição" onClick={() => removeRule(index)}>
                  <DeleteIcon />
                </IconButton>
              }
            >
              <ListItemText primary={describeMatch(rule)} sx={{ paddingRight: 5 }} />
            </ListItem>
          ))}
        </List>
      )}

      <Grid container spacing={3}>
        <Grid item md={6} xs={12}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel id="failure-condition-type-label">Condição</InputLabel>
              <Select
                labelId="failure-condition-type-label"
                label="Condição"
                value={matchType}
                onChange={(e) => setMatchType(e.target.value as MatchType)}
              >
                <MenuItem value="empty_content" disabled={hasEmptyContentRule}>Quando o retorno vier vazio</MenuItem>
                <MenuItem value="field_value">Quando um campo específico tiver um valor</MenuItem>
                <MenuItem value="status_code">Quando o código de status for</MenuItem>
              </Select>
            </FormControl>

            {matchType === 'field_value' && (
              <>
                <TextField
                  fullWidth
                  label="Caminho do campo"
                  placeholder="Ex: RESULTADO.STATUS"
                  value={contentPath}
                  onChange={(e) => setContentPath(e.target.value)}
                  helperText="O caminho até o campo dentro da resposta da API, separado por pontos."
                />
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={valueIn}
                  onChange={(_, newValue) => setValueIn(newValue as string[])}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Valores aceitos"
                      helperText="Digite um valor e aperte Enter. Pode adicionar mais de um."
                    />
                  )}
                />
              </>
            )}

            {matchType === 'status_code' && (
              <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={statusCodes}
                onChange={(_, newValue) => setStatusCodes(newValue as string[])}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Códigos de status"
                    helperText="Digite um código (ex: 503) e aperte Enter. Pode adicionar mais de um."
                  />
                )}
              />
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={requeryEligible}
                  onChange={(e) => setRequeryEligible(e.target.checked)}
                />
              }
              label="Permitir reconsulta"
            />

            <Button variant="contained" color="primary" onClick={addRule} disabled={addDisabled}>
              Adicionar condição
            </Button>
          </Box>
        </Grid>

        <Grid item md={6} xs={12}>
          <JsonCodeEditor
            label="Estrutura JSON"
            value={rulesText}
            onChange={handleRulesTextChange}
            helperText={jsonError ?? undefined}
            error={!!jsonError}
            minHeight="80px"
          />
        </Grid>
      </Grid>
    </Box>
  )
}
